import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, ShieldCheck, X } from "lucide-react";
import { DEFAULT_PREFS, applyConsent, getStoredConsent, storeConsent } from "@/lib/consent";

const OPEN_EVENT = "riq:open-cookie-settings";

/** Allows any element (e.g. the footer link) to reopen the preferences dialog. */
export function openCookieSettings() {
  if (typeof window !== "undefined") window.dispatchEvent(new Event(OPEN_EVENT));
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showManage, setShowManage] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);

  useEffect(() => {
    const stored = getStoredConsent();
    if (!stored) {
      setVisible(true);
    }
    const onOpen = () => {
      const s = getStoredConsent();
      if (s) setPrefs({ ...DEFAULT_PREFS, ...s });
      setShowManage(true);
      setVisible(true);
    };
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, []);

  const commit = (choice) => {
    storeConsent(choice);
    applyConsent(choice);
    setVisible(false);
    setShowManage(false);
  };

  const acceptAll = () => commit({ necessary: true, analytics: true, marketing: true });
  const rejectAll = () => commit({ necessary: true, analytics: false, marketing: false });
  const savePrefs = () => commit({ ...prefs, necessary: true });

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[2147483647] p-3 sm:p-4"
      data-testid="cookie-consent"
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
    >
      <div className="max-w-5xl mx-auto bg-[#111827] text-slate-200 border border-white/10 shadow-2xl shadow-black/50 rounded-xl overflow-hidden">
        {!showManage ? (
          <div className="p-5 sm:p-6 flex flex-col lg:flex-row lg:items-center gap-5">
            <div className="flex items-start gap-3 flex-1">
              <span className="w-10 h-10 grid place-items-center bg-[#16A34A]/15 text-[#16A34A] rounded-lg shrink-0">
                <Cookie className="w-5 h-5" />
              </span>
              <div className="text-sm leading-relaxed">
                <div className="font-heading font-semibold text-white text-base">We value your privacy</div>
                <p className="mt-1 text-slate-300">
                  We use essential cookies to make this site work. With your consent we also use analytics
                  cookies (Google Analytics &amp; PostHog) to understand how the site is used and improve it.
                  No analytics or marketing tracking runs until you accept. Read our{" "}
                  <Link to="/cookie-policy" className="text-[#16A34A] underline hover:text-[#22c55e]">Cookie Policy</Link>{" "}
                  and{" "}
                  <Link to="/privacy-policy" className="text-[#16A34A] underline hover:text-[#22c55e]">Privacy Policy</Link>.
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2.5 shrink-0">
              <button
                type="button"
                data-testid="cookie-manage"
                onClick={() => { setPrefs({ ...DEFAULT_PREFS, ...(getStoredConsent() || {}) }); setShowManage(true); }}
                className="min-h-[44px] px-5 py-2.5 text-xs font-semibold uppercase tracking-wider border border-white/20 text-white hover:bg-white/10 rounded-lg transition-colors"
              >
                Manage preferences
              </button>
              <button
                type="button"
                data-testid="cookie-reject"
                onClick={rejectAll}
                className="min-h-[44px] px-5 py-2.5 text-xs font-semibold uppercase tracking-wider border border-white/20 text-white hover:bg-white/10 rounded-lg transition-colors"
              >
                Reject all
              </button>
              <button
                type="button"
                data-testid="cookie-accept"
                onClick={acceptAll}
                className="min-h-[44px] px-6 py-2.5 text-xs font-semibold uppercase tracking-wider bg-[#16A34A] hover:bg-[#15803d] text-white rounded-lg transition-colors"
              >
                Accept all
              </button>
            </div>
          </div>
        ) : (
          <div className="p-5 sm:p-6" data-testid="cookie-manage-panel">
            <div className="flex items-start justify-between gap-4">
              <div className="font-heading font-semibold text-white text-base">Manage cookie preferences</div>
              <button
                type="button"
                onClick={() => (getStoredConsent() ? (setShowManage(false), setVisible(false)) : setShowManage(false))}
                aria-label="Close cookie preferences"
                className="text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mt-4 space-y-3">
              <PrefRow
                title="Strictly necessary"
                desc="Required for security and core site functionality. These are always active and cannot be switched off."
                checked
                disabled
                icon={<ShieldCheck className="w-4 h-4" />}
              />
              <PrefRow
                title="Analytics"
                desc="Google Analytics 4 and PostHog help us understand how visitors use the site so we can improve it."
                checked={prefs.analytics}
                testid="cookie-toggle-analytics"
                onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
              />
              <PrefRow
                title="Marketing"
                desc="Reserved for future advertising or remarketing tags. Currently off by default."
                checked={prefs.marketing}
                testid="cookie-toggle-marketing"
                onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
              />
            </div>

            <div className="mt-5 flex flex-col sm:flex-row gap-2.5 justify-end">
              <button
                type="button"
                onClick={rejectAll}
                className="min-h-[44px] px-5 py-2.5 text-xs font-semibold uppercase tracking-wider border border-white/20 text-white hover:bg-white/10 rounded-lg transition-colors"
              >
                Reject all
              </button>
              <button
                type="button"
                data-testid="cookie-save"
                onClick={savePrefs}
                className="min-h-[44px] px-6 py-2.5 text-xs font-semibold uppercase tracking-wider bg-[#16A34A] hover:bg-[#15803d] text-white rounded-lg transition-colors"
              >
                Save preferences
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PrefRow({ title, desc, checked, disabled, onChange, icon, testid }) {
  return (
    <div className="flex items-start justify-between gap-4 bg-white/5 border border-white/10 rounded-lg p-4">
      <div className="text-sm">
        <div className="flex items-center gap-2 font-semibold text-white">
          {icon}
          {title}
        </div>
        <p className="mt-1 text-slate-400 leading-relaxed">{desc}</p>
      </div>
      <label className={`relative inline-flex items-center shrink-0 mt-1 ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}>
        <input
          type="checkbox"
          className="sr-only peer"
          checked={checked}
          disabled={disabled}
          data-testid={testid}
          onChange={(e) => onChange && onChange(e.target.checked)}
        />
        <span className="w-11 h-6 bg-slate-600 rounded-full peer-checked:bg-[#16A34A] transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-5 after:h-5 after:bg-white after:rounded-full after:transition-transform peer-checked:after:translate-x-5" />
      </label>
    </div>
  );
}
